import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import preferences from 'Preferences';
import {SchemaPreferences} from 'schemas/SchemaConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import {SelectOptionModalBody} from 'ui-library/modals/SelectOptionModal';
import {listToReadable} from 'util/NameUtil';

const CONVERT_FIELDS_OPTION = 'CONVERT_FIELDS_OPTION';
const DELETE_FIELDS_OPTION = 'DELETE_FIELDS_OPTION';

interface Props {
  convertableFieldNames: Array<string>;
  onRequestClose: () => void;
  onSubmit: (submitArgs: {deleteOldSchemaTags: boolean}) => void;
  forMixtures: boolean;
  mayDropConvertedFromLinks?: boolean;
}

interface State {
  selectedOptionForTags: string;
  confirmedMixtureReset: boolean;
}

export default class ConfirmSetTagSchemaModal extends React.Component<Props, State> {
  static open(props: Omit<Props, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      ConfirmSetTagSchemaModal,
      <ConfirmSetTagSchemaModal
        onRequestClose={() => {
          ConfirmSetTagSchemaModal.close();
        }}
        {...props}
      />
    );
  }

  static close(): void {
    LegacyModalContainer.close(ConfirmSetTagSchemaModal);
  }

  override state: State = {
    selectedOptionForTags: preferences.get(SchemaPreferences.SET_TAG_SCHEMA_MODAL_OPTION),
    confirmedMixtureReset: !this.props.forMixtures,
  };

  private handleSubmit(): void {
    preferences.set(SchemaPreferences.SET_TAG_SCHEMA_MODAL_OPTION, this.state.selectedOptionForTags);
    const deleteOldSchemaTags = this.state.selectedOptionForTags === DELETE_FIELDS_OPTION;
    this.props.onSubmit({deleteOldSchemaTags});
    this.props.onRequestClose();
  }

  private renderMixtureResetWarning(includeTopMargin: boolean): JSX.Element {
    return (
      <div className={css(includeTopMargin && styles.resetWarningWithTopMargin)}>
        <div>
          Mixture ingredients will be deleted and mixture amount will be re-set. Please confirm that you wish
          to proceed
        </div>
        <input
          type="checkbox"
          className={css(styles.confirmResetCheckbox)}
          onChange={(e) => {
            this.setState({confirmedMixtureReset: e.target.checked});
          }}
        />
        I understand.
      </div>
    );
  }

  private renderConvertFieldsWarning(): JSX.Element {
    const fieldNamesString = listToReadable(
      this.props.convertableFieldNames.map((fieldName) => `"${fieldName}"`)
    );
    const preamble = (
      <span>
        {`The following fields are not present on the schema you are changing to: ${fieldNamesString}`}
        {this.props.mayDropConvertedFromLinks && (
          <b>
            <br />
            <br />
            Converted from linkage and associated metadata may be lost.
          </b>
        )}
      </span>
    );
    return (
      <SelectOptionModalBody
        selectedOption={this.state.selectedOptionForTags}
        options={[
          {text: 'Convert these to custom fields.', value: CONVERT_FIELDS_OPTION},
          {
            text: 'Delete these fields. This action cannot be undone.',
            value: DELETE_FIELDS_OPTION,
          },
        ]}
        preamble={preamble}
        onSelectOption={(selectedOption: string) => {
          this.setState({selectedOptionForTags: selectedOption});
        }}
      />
    );
  }

  override render(): JSX.Element {
    const hasConvertableFieldNames = this.props.convertableFieldNames.length > 0;
    assert(
      hasConvertableFieldNames || this.props.forMixtures,
      'ConfirmSetTagSchemaModal should only be shown if there are existing tag values or the schema change includes mixture files'
    );
    return (
      <Modal
        title="Change schema?"
        onRequestSubmit={() => {
          this.handleSubmit();
        }}
        onRequestClose={this.props.onRequestClose}
        wrapInForm={false}
        showFooterBorder={false}
        buttons={[
          {
            disabled: !this.state.confirmedMixtureReset,
            text: 'Done',
            submit: true,
          },
          {text: 'Cancel', cancel: true},
        ]}
      >
        {hasConvertableFieldNames && this.renderConvertFieldsWarning()}
        {this.props.forMixtures && this.renderMixtureResetWarning(hasConvertableFieldNames)}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  resetWarningWithTopMargin: {
    marginTop: '20px',
  },
  confirmResetCheckbox: {
    marginTop: '8px',
    marginRight: '6px',
  },
});
